/**
 * Admin Audit Log Viewer
 *
 * Internal admin tool for rp_admin and rp_support staff to investigate issues,
 * monitor security, and troubleshoot customer problems.
 *
 * Features:
 * - 7 filter types (date range, event types, user, org, source, IP, free-text)
 * - Expandable table rows with JSON viewer
 * - Sortable columns
 * - Pagination with page size selector
 * - Export to CSV/JSON/NDJSON
 * - URL state sync for shareable views
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  DatePicker,
  Select,
  Input,
  Button,
  Tag,
  Space,
  Pagination,
  Dropdown,
  Card,
  Badge,
  Typography,
  message,
  Tooltip,
  Table,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ExpandOutlined,
} from '@ant-design/icons'
import ReactJson from '@microlink/react-json-view'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useSearchParams } from 'react-router-dom'

import { auditLogService } from '@/services/auditLogService'
import type {
  AuditLogFilters,
  PaginationState,
  SortState,
  EventStatus,
  EnrichedAuditLog,
} from '@/types/auditLog.types'
import {
  EventTypeCategories,
  SourceOptions,
  DateRangePresets,
} from '@/types/auditLog.types'

// ===================================
// Dynamic Filter Options Hook
// ===================================

/**
 * Build grouped event type options for the Select dropdown.
 * Uses static categories for grouping but marks which types have actual data.
 * Unknown types (in data but not in categories) go to "Other" category.
 */
function buildEventTypeOptions(actualEventTypes: string[]): {
  label: string;
  options: { label: string; value: string }[];
}[] {
  const knownTypes = new Set<string>(Object.values(EventTypeCategories).flat())

  // Build options from static categories
  const categoryOptions: { label: string; options: { label: string; value: string }[] }[] =
    Object.entries(EventTypeCategories).map(([category, events]) => ({
      label: category,
      options: events.map(event => ({
        label: event,
        value: event,
      })),
    }))

  // Find unknown types (in actual data but not in any category)
  const unknownTypes = actualEventTypes.filter(type => !knownTypes.has(type))

  // Add "Other" category if there are unknown types
  if (unknownTypes.length > 0) {
    categoryOptions.push({
      label: 'Other',
      options: unknownTypes.map(type => ({
        label: type,
        value: type,
      })),
    })
  }

  return categoryOptions
}

/**
 * Build source options, combining static list with any dynamic values.
 * Unknown sources go at the end.
 */
function buildSourceOptions(actualSources: string[]): { label: string; value: string }[] {
  const staticSet = new Set(SourceOptions)

  // Start with static options
  const options: { label: string; value: string }[] = SourceOptions.map(source => ({
    label: source,
    value: source,
  }))

  // Add any unknown sources found in data
  const unknownSources = actualSources.filter(source => !staticSet.has(source as any))
  unknownSources.forEach(source => {
    options.push({ label: source, value: source })
  })

  // Sort alphabetically by label
  options.sort((a, b) => a.label.localeCompare(b.label))

  return options
}

dayjs.extend(utc)

const { RangePicker } = DatePicker
const { Title, Text } = Typography

// ===================================
// Helper Functions
// ===================================

/**
 * Determine event status from event type
 */
function getEventStatus(eventType: string): EventStatus {
  const errorEvents = [
    'AUTHENTICATION_FAILED',
    'TOKEN_REFRESH_FAILED',
    'INVALID_TOKEN_DETECTED',
    'AUTHORIZATION_DENIED',
    'WEBHOOK_FAILED',
    'CONNECTION_HEALTH_CHECK_FAILED',
    'COGNITO_USER_CREATE_FAILED',
    'COGNITO_USER_ROLLBACK_FAILED',
  ]

  const warningEvents = [
    'SESSION_TIMEOUT',
    'SIGNUP_QUARANTINED',
    'SIGNUP_BLOCKED_BLACKLIST',
    'AUTHORIZATION_CONTEXT_MISMATCH',
    'WEBHOOK_RETRY',
  ]

  if (errorEvents.some(e => eventType.includes(e))) return 'error'
  if (warningEvents.some(e => eventType.includes(e))) return 'warning'
  return 'success'
}

/**
 * Get status indicator icon
 */
function getStatusIcon(status: EventStatus): React.ReactNode {
  switch (status) {
    case 'success':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />
    case 'warning':
      return <WarningOutlined style={{ color: '#faad14' }} />
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
  }
}

/**
 * Copy text to clipboard
 */
function copyToClipboard(text: string, label: string): void {
  navigator.clipboard.writeText(text)
  message.success(`${label} copied to clipboard`)
}

// ===================================
// Main Component
// ===================================

export default function AuditLogsPage() {
  const [, setSearchParams] = useSearchParams()

  // State management
  const [filters, setFilters] = useState<AuditLogFilters>({
    dateRange: null,
    eventTypes: [],
    userName: undefined,
    userId: undefined,
    orgName: undefined,
    orgId: undefined,
    source: undefined,
    ipAddress: undefined,
  })

  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 50,
    total: 0,
  })

  const [sort, setSort] = useState<SortState>({
    field: 'createdAt',
    direction: 'desc',
  })

  const [filterPanelVisible, setFilterPanelVisible] = useState(true)

  // Fetch distinct event types for filter dropdown (cached for 1 hour - rarely changes)
  const { data: eventTypesData } = useQuery({
    queryKey: ['audit-log-event-types'],
    queryFn: async () => {
      const response = await auditLogService.fetchEventTypes()
      if (!response.success) {
        console.warn('Failed to fetch event types, using static list')
        return []
      }
      return response.data || []
    },
    staleTime: 1000 * 60 * 60, // 1 hour - event types change rarely
  })

  // Fetch distinct sources for filter dropdown (cached for 1 hour - rarely changes)
  const { data: sourcesData } = useQuery({
    queryKey: ['audit-log-sources'],
    queryFn: async () => {
      const response = await auditLogService.fetchSources()
      if (!response.success) {
        console.warn('Failed to fetch sources, using static list')
        return []
      }
      return response.data || []
    },
    staleTime: 1000 * 60 * 60, // 1 hour - sources change rarely
  })

  // Build dynamic filter options (memoized)
  const eventTypeOptions = useMemo(
    () => buildEventTypeOptions(eventTypesData || []),
    [eventTypesData]
  )

  const sourceOptions = useMemo(
    () => buildSourceOptions(sourcesData || []),
    [sourcesData]
  )

  // Fetch audit logs with React Query
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-logs', filters, pagination.current, pagination.pageSize, sort],
    queryFn: async () => {
      const response = await auditLogService.fetchAuditLogs(
        filters,
        { page: pagination.current, pageSize: pagination.pageSize },
        sort
      )

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch audit logs')
      }

      return response.data!
    },
    staleTime: 1000 * 60, // 1 minute - audit logs update frequently
  })

  // Update total count when data changes
  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setPagination(prev => ({ ...prev, total: data.totalCount }))
    }
  }, [data?.totalCount])

  // URL state sync (for shareable filtered views)
  useEffect(() => {
    const params = new URLSearchParams()

    if (filters.dateRange) {
      params.set('dateFrom', filters.dateRange[0])
      params.set('dateTo', filters.dateRange[1])
    }
    if (filters.eventTypes && filters.eventTypes.length > 0) {
      params.set('eventTypes', filters.eventTypes.join(','))
    }
    if (filters.userName) params.set('userName', filters.userName)
    if (filters.userId) params.set('userId', filters.userId)
    if (filters.orgName) params.set('orgName', filters.orgName)
    if (filters.orgId) params.set('orgId', filters.orgId)
    if (filters.source) params.set('source', filters.source)
    if (filters.ipAddress) params.set('ipAddress', filters.ipAddress)
    params.set('page', pagination.current.toString())
    params.set('pageSize', pagination.pageSize.toString())
    params.set('sortBy', sort.field)
    params.set('sortDir', sort.direction)

    setSearchParams(params, { replace: true })
  }, [filters, pagination.current, pagination.pageSize, sort, setSearchParams])

  // ===================================
  // Event Handlers
  // ===================================

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, current: 1 })) // Reset to page 1 on filter change
  }

  const handleClearFilters = () => {
    setFilters({
      dateRange: null,
      eventTypes: [],
      userId: undefined,
      orgId: undefined,
      source: undefined,
      ipAddress: undefined,
    })
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (
    _pagination: any,
    _filters: any,
    sorter: any
  ) => {
    if (sorter.field && sorter.order) {
      // User explicitly set a sort direction
      setSort({
        field: sorter.field,
        direction: sorter.order === 'ascend' ? 'asc' : 'desc',
      })
    } else if (sort.field === 'createdAt') {
      // Timestamp was sorted and user clicked to clear/cycle - toggle direction
      // (Use current state 'sort.field' since sorter.field may be undefined when clearing)
      setSort({
        field: 'createdAt',
        direction: sort.direction === 'desc' ? 'asc' : 'desc',
      })
    } else {
      // Another column was sorted and user cleared it - return to default
      setSort({
        field: 'createdAt',
        direction: 'desc',
      })
    }
  }

  const handleExport = async (format: 'csv' | 'json' | 'ndjson', scope: 'currentPage' | 'allFiltered') => {
    try {
      let logsToExport: EnrichedAuditLog[] = []

      if (scope === 'currentPage') {
        logsToExport = data?.events || []
      } else {
        // Fetch all filtered results
        const response = await auditLogService.fetchAuditLogs(
          filters,
          { page: 1, pageSize: 10000 }, // Large limit for export
          sort
        )
        if (response.success && response.data) {
          logsToExport = response.data.events
        }
      }

      if (logsToExport.length === 0) {
        message.warning('No data to export')
        return
      }

      const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss')
      let content: string
      let filename: string
      let mimeType: string

      switch (format) {
        case 'csv':
          content = auditLogService.exportToCSV(logsToExport)
          filename = `audit-logs_${timestamp}.csv`
          mimeType = 'text/csv'
          break
        case 'json':
          content = auditLogService.exportToJSON(logsToExport)
          filename = `audit-logs_${timestamp}.json`
          mimeType = 'application/json'
          break
        case 'ndjson':
          content = auditLogService.exportToNDJSON(logsToExport)
          filename = `audit-logs_${timestamp}.ndjson`
          mimeType = 'application/x-ndjson'
          break
      }

      auditLogService.downloadExport(content, filename, mimeType)
      message.success(`Exported ${logsToExport.length} audit logs as ${format.toUpperCase()}`)
    } catch (err) {
      message.error('Failed to export audit logs')
      console.error(err)
    }
  }

  // ===================================
  // Table Columns
  // ===================================

  const columns: ColumnsType<EnrichedAuditLog> = useMemo(() => [
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: (_: any, record: EnrichedAuditLog) => {
        const status = getEventStatus(record.eventType)
        return (
          <Tooltip title={status}>
            {getStatusIcon(status)}
          </Tooltip>
        )
      },
    },
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder: sort.field === 'createdAt' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (_, record) => dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 200,
      sorter: true,
      sortOrder: sort.field === 'eventType' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (_, record) => (
        <Tag color="blue">{record.eventType}</Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      width: 150,
      sorter: true,
      sortOrder: sort.field === 'userName' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (_, record) => {
        const { userId, userName, userEmail } = record
        if (!userId) return <Text type="secondary">System</Text>
        const displayName = userName || userId.slice(0, 8) + '...'
        const tooltipContent = userEmail ? `${userEmail}\n${userId}` : userId
        return (
          <Tooltip title={tooltipContent}>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(userId, 'User ID')}
            >
              {displayName}
            </Button>
          </Tooltip>
        )
      },
    },
    {
      title: 'Organization',
      dataIndex: 'orgName',
      key: 'orgName',
      width: 150,
      sorter: true,
      sortOrder: sort.field === 'orgName' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (_, record) => {
        const { orgId, orgName } = record
        if (!orgId) return <Text type="secondary">Global</Text>
        const displayName = orgName || orgId.slice(0, 8) + '...'
        return (
          <Tooltip title={orgId}>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(orgId, 'Org ID')}
            >
              {displayName}
            </Button>
          </Tooltip>
        )
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 180,
      sorter: true,
      sortOrder: sort.field === 'source' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      sorter: true,
      sortOrder: sort.field === 'ipAddress' ? (sort.direction === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (_, record) => record.ipAddress || <Text type="secondary">-</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: EnrichedAuditLog) => (
        <Space>
          <Tooltip title="Copy Event UID">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(String(record.uid), 'Event UID')}
            />
          </Tooltip>
        </Space>
      ),
    },
  ], [sort])


  // ===================================
  // Render
  // ===================================

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Audit Log Viewer</Title>
        <Space>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setFilterPanelVisible(!filterPanelVisible)}
          >
            {filterPanelVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'csv-page',
                  label: 'CSV (Current Page)',
                  onClick: () => handleExport('csv', 'currentPage'),
                },
                {
                  key: 'csv-all',
                  label: 'CSV (All Filtered)',
                  onClick: () => handleExport('csv', 'allFiltered'),
                },
                { type: 'divider' },
                {
                  key: 'json-page',
                  label: 'JSON (Current Page)',
                  onClick: () => handleExport('json', 'currentPage'),
                },
                {
                  key: 'json-all',
                  label: 'JSON (All Filtered)',
                  onClick: () => handleExport('json', 'allFiltered'),
                },
                { type: 'divider' },
                {
                  key: 'ndjson-page',
                  label: 'NDJSON (Current Page)',
                  onClick: () => handleExport('ndjson', 'currentPage'),
                },
                {
                  key: 'ndjson-all',
                  label: 'NDJSON (All Filtered)',
                  onClick: () => handleExport('ndjson', 'allFiltered'),
                },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />}>Export</Button>
          </Dropdown>
        </Space>
      </div>

      {/* Filter Panel */}
      {filterPanelVisible && (
        <Card style={{ marginBottom: '24px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap style={{ width: '100%' }}>
              {/* Date Range */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Date Range:</Text>
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  value={
                    filters.dateRange
                      ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates) {
                      handleFilterChange('dateRange', [
                        dates[0]!.toISOString(),
                        dates[1]!.toISOString(),
                      ])
                    } else {
                      handleFilterChange('dateRange', null)
                    }
                  }}
                  presets={DateRangePresets.map(preset => ({
                    label: preset.label,
                    value: [
                      dayjs().subtract(preset.value, preset.unit),
                      dayjs(),
                    ],
                  }))}
                  style={{ width: 400 }}
                />
              </div>

              {/* Event Types */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Event Types:</Text>
                <Select
                  mode="multiple"
                  placeholder="Select event types"
                  value={filters.eventTypes}
                  onChange={(value) => handleFilterChange('eventTypes', value)}
                  style={{ width: 300 }}
                  maxTagCount="responsive"
                  options={eventTypeOptions}
                />
              </div>

              {/* Source */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Source:</Text>
                <Select
                  placeholder="Select source"
                  value={filters.source}
                  onChange={(value) => handleFilterChange('source', value)}
                  allowClear
                  style={{ width: 200 }}
                  options={sourceOptions}
                />
              </div>
            </Space>

            <Space wrap style={{ width: '100%' }}>
              {/* User Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>User Name:</Text>
                <Input
                  placeholder="Search by name"
                  value={filters.userName}
                  onChange={(e) => handleFilterChange('userName', e.target.value || undefined)}
                  style={{ width: 180 }}
                  allowClear
                />
              </div>

              {/* User ID */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>User ID:</Text>
                <Input
                  placeholder="Enter user ID"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value || undefined)}
                  style={{ width: 250 }}
                  allowClear
                />
              </div>

              {/* Organization Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Org Name:</Text>
                <Input
                  placeholder="Search by name"
                  value={filters.orgName}
                  onChange={(e) => handleFilterChange('orgName', e.target.value || undefined)}
                  style={{ width: 180 }}
                  allowClear
                />
              </div>

              {/* Organization ID */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Organization ID:</Text>
                <Input
                  placeholder="Enter org ID (empty for global)"
                  value={filters.orgId}
                  onChange={(e) => handleFilterChange('orgId', e.target.value || undefined)}
                  style={{ width: 250 }}
                  allowClear
                />
              </div>

              {/* IP Address */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>IP Address:</Text>
                <Input
                  placeholder="Enter IP address"
                  value={filters.ipAddress}
                  onChange={(e) => handleFilterChange('ipAddress', e.target.value || undefined)}
                  style={{ width: 180 }}
                  allowClear
                />
              </div>

            </Space>

            <div>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
              >
                Clear All Filters
              </Button>
            </div>
          </Space>
        </Card>
      )}

      {/* Active Filters Display */}
      {(filters.dateRange ||
        (filters.eventTypes && filters.eventTypes.length > 0) ||
        filters.userName ||
        filters.userId ||
        filters.orgName ||
        filters.orgId ||
        filters.source ||
        filters.ipAddress) && (
        <div style={{ marginBottom: '16px' }}>
          <Space wrap>
            <Text strong>Active Filters:</Text>
            {filters.dateRange && (
              <Tag
                closable
                onClose={() => handleFilterChange('dateRange', null)}
              >
                Date: {dayjs(filters.dateRange[0]).format('YYYY-MM-DD')} to {dayjs(filters.dateRange[1]).format('YYYY-MM-DD')}
              </Tag>
            )}
            {filters.eventTypes?.map(type => (
              <Tag
                key={type}
                closable
                onClose={() => handleFilterChange('eventTypes', filters.eventTypes?.filter(t => t !== type))}
              >
                Event: {type}
              </Tag>
            ))}
            {filters.userName && (
              <Tag closable onClose={() => handleFilterChange('userName', undefined)}>
                User Name: {filters.userName}
              </Tag>
            )}
            {filters.userId && (
              <Tag closable onClose={() => handleFilterChange('userId', undefined)}>
                User ID: {filters.userId.slice(0, 8)}...
              </Tag>
            )}
            {filters.orgName && (
              <Tag closable onClose={() => handleFilterChange('orgName', undefined)}>
                Org Name: {filters.orgName}
              </Tag>
            )}
            {filters.orgId && (
              <Tag closable onClose={() => handleFilterChange('orgId', undefined)}>
                Org ID: {filters.orgId.slice(0, 8)}...
              </Tag>
            )}
            {filters.source && (
              <Tag closable onClose={() => handleFilterChange('source', undefined)}>
                Source: {filters.source}
              </Tag>
            )}
            {filters.ipAddress && (
              <Tag closable onClose={() => handleFilterChange('ipAddress', undefined)}>
                IP: {filters.ipAddress}
              </Tag>
            )}
          </Space>
        </div>
      )}

      {/* Results Header */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>
          <Badge count={pagination.total} showZero color="blue" />
          <span style={{ marginLeft: '8px' }}>
            {pagination.total === 1 ? 'audit log' : 'audit logs'}
          </span>
        </Text>
        <Space>
          <Text>Page Size:</Text>
          <Select
            value={pagination.pageSize}
            onChange={(value) => setPagination(prev => ({ ...prev, pageSize: value, current: 1 }))}
            style={{ width: 80 }}
            options={[
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </Space>
      </div>

      {/* Results Table */}
      <Table<EnrichedAuditLog>
        columns={columns}
        dataSource={data?.events || []}
        loading={isLoading}
        rowKey="uid"
        pagination={false}
        onChange={handleTableChange}
        style={{ tableLayout: 'fixed' }}
        expandable={{
          expandedRowRender: (record: EnrichedAuditLog) => (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong>Event Details:</Text>
                  <div style={{ marginTop: '8px', maxHeight: '400px', overflow: 'auto' }}>
                    <ReactJson
                      src={record.eventDetails}
                      theme="rjv-default"
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard
                    />
                  </div>
                </div>
                <div>
                  <Text strong>Metadata:</Text>
                  <div style={{ marginTop: '8px', maxHeight: '400px', overflow: 'auto' }}>
                    <ReactJson
                      src={record.metadata}
                      theme="rjv-default"
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard
                    />
                  </div>
                </div>
                <div>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(JSON.stringify(record, null, 2), 'Full Event JSON')}
                  >
                    Copy Full Event JSON
                  </Button>
                </div>
              </Space>
            </div>
          ),
          expandIcon: ({ onExpand, record }) => (
            <Button
              type="text"
              size="small"
              icon={<ExpandOutlined />}
              onClick={(e) => onExpand(record, e)}
            />
          ),
        }}
      />

      {/* Pagination */}
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page) => setPagination(prev => ({ ...prev, current: page }))}
          showSizeChanger={false}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
        />
      </div>
    </div>
  )
}
